import { RootState } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import {
  getPlayerData,
  selectIsMyEntry,
} from "core-integration/src/store/player/reducers";
import { EntryCrestData } from "core-integration/src/store/ui";
import { Button } from "plos/src/components/buttons/Button";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ReportCrestButton from "../../../ReportCrestButton";
import { crestActionsWrapper } from "../../crestStyles.css";

interface CrestSheetActionProps {
  enabled: boolean;
  entryCrestData: EntryCrestData;
  hasPendingCrest: boolean;
  isMobile: boolean;
  handleCreateCrest: (id?: number) => void;
  isNewEntry?: boolean;
  handleClose?: () => void;
}

const CrestSheetAction = ({
  enabled,
  entryCrestData,
  hasPendingCrest,
  isMobile,
  handleCreateCrest,
  isNewEntry,
  handleClose,
}: CrestSheetActionProps) => {
  const { t } = useTranslation();
  const mine = useSelector((state: RootState) =>
    selectIsMyEntry(state, entryCrestData.id),
  );
  const player = useSelector((state: RootState) => getPlayerData(state));
  const playerEntry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );

  // During the adobe flow in the new entry flow, show edit / keep my badge buttons
  if (isNewEntry) {
    return (
      <div className={crestActionsWrapper}>
        {enabled && playerEntry && playerEntry.club_badge_src !== "Pending" && (
          <Button
            appearance="outlined"
            size="small"
            fullWidth
            onPress={() => handleCreateCrest(playerEntry.id)}
          >
            {t("crestSheetAction.editMyBadge", "Edit My Badge")}
          </Button>
        )}
        <Button size="small" fullWidth onPress={handleClose}>
          {t("crestSheetAction.keepMyBadge", "Keep My Badge")}
        </Button>
      </div>
    );
  }

  if (!enabled && !isMobile) {
    return (
      <div style={{ textAlign: "center" }}>
        <span>
          {t(
            "crestSheetAction.adobeExpressUnavailable",
            "Adobe Express is currently unavailable",
          )}
        </span>
      </div>
    );
  }

  if (!enabled || hasPendingCrest) return null;

  // Viewing another players badge shows report button and create/edit their own badge
  if (!mine) {
    return (
      <div className={crestActionsWrapper}>
        <ReportCrestButton entryId={entryCrestData.id} />
        {playerEntry && playerEntry.club_badge_src !== "Pending" && (
          <Button
            appearance="outlined"
            size="small"
            fullWidth
            onPress={() => handleCreateCrest(playerEntry.id)}
          >
            {playerEntry.club_badge_src
              ? t("crestSheetAction.editMyBadge", "Edit My Badge")
              : t("crestSheetAction.createMyBadge", "Create My Badge")}
          </Button>
        )}
      </div>
    );
  }

  // My entry - show only edit/create button
  return (
    <Button
      appearance="outlined"
      size="small"
      fullWidth
      onPress={() => handleCreateCrest(entryCrestData.id)}
    >
      {entryCrestData.club_badge_src
        ? t("crestSheetAction.editMyBadge", "Edit My Badge")
        : t("crestSheetAction.createMyBadge", "Create My Badge")}
    </Button>
  );
};

export default CrestSheetAction;
